import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { formatResultDateLabel } from "../../services/resultsMapper";
import styles from "./DrawDatePicker.module.css";

const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function monthLabel(monthKey) {
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!year || !month) return "";
  return `${MONTHS[month - 1]} ${year}`;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function mondayOffset(year, month) {
  const weekday = new Date(year, month - 1, 1).getDay();
  return (weekday + 6) % 7;
}

/**
 * Calendario de fechas de sorteo. Solo se pueden elegir días con resultado.
 */
const DrawDatePicker = ({
  dates = [],
  value = "",
  onChange,
  id = "draw-date",
  placeholder = "Seleccionar fecha",
}) => {
  const marked = useMemo(() => new Set(dates), [dates]);
  const months = useMemo(
    () => [...new Set(dates.map((key) => String(key).slice(0, 7)).filter(Boolean))].sort(),
    [dates]
  );

  const [open, setOpen] = useState(false);
  const [monthKey, setMonthKey] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (value && months.includes(value.slice(0, 7))) {
      setMonthKey(value.slice(0, 7));
      return;
    }
    if (months.length > 0) setMonthKey(months[months.length - 1]);
  }, [value, months]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const monthIndex = months.indexOf(monthKey);
  const [year, month] = monthKey.split("-").map(Number);
  const blanks = year && month ? mondayOffset(year, month) : 0;
  const totalDays = year && month ? daysInMonth(year, month) : 0;

  const pickDay = (day) => {
    const key = `${monthKey}-${String(day).padStart(2, "0")}`;
    if (!marked.has(key)) return;
    onChange?.(key);
    setOpen(false);
  };

  const empty = dates.length === 0;
  const label = value ? formatResultDateLabel(value) : placeholder;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`${styles.trigger} ${value ? styles.triggerFilled : ""}`}
        onClick={() => !empty && setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={empty}
      >
        <span>{empty ? "Sin fechas de sorteo" : label}</span>
        <FontAwesomeIcon icon={faCalendarDays} className={styles.icon} />
      </button>

      {open && monthKey && (
        <div className={styles.panel} role="dialog" aria-label="Seleccionar fecha de sorteo">
          <div className={styles.monthBar}>
            <button
              type="button"
              className={styles.monthNav}
              onClick={() => monthIndex > 0 && setMonthKey(months[monthIndex - 1])}
              disabled={monthIndex <= 0}
              aria-label="Mes anterior"
            >
              ‹
            </button>
            <p className={styles.monthTitle}>{monthLabel(monthKey)}</p>
            <button
              type="button"
              className={styles.monthNav}
              onClick={() => monthIndex < months.length - 1 && setMonthKey(months[monthIndex + 1])}
              disabled={monthIndex >= months.length - 1}
              aria-label="Mes siguiente"
            >
              ›
            </button>
          </div>

          <div className={styles.weekdays}>
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className={styles.grid}>
            {Array.from({ length: blanks }, (_, index) => (
              <span key={`b-${index}`} className={styles.blank} />
            ))}
            {Array.from({ length: totalDays }, (_, index) => {
              const day = index + 1;
              const key = `${monthKey}-${String(day).padStart(2, "0")}`;
              const hasDraw = marked.has(key);
              const selected = value === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.day} ${hasDraw ? styles.dayMarked : ""} ${
                    selected ? styles.daySelected : ""
                  }`}
                  disabled={!hasDraw}
                  onClick={() => pickDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <p className={styles.hint}>Los días marcados tienen sorteo.</p>
        </div>
      )}
    </div>
  );
};

export default DrawDatePicker;
